<template>
	<div class="marketmath-single-survey">
		<app-page>
			<app-heading :title="isNew ? 'New Survey' : survey.name" />
			<app-form @submit="onSubmit">
				<app-section class="mt-2 mb-2">
					<app-section-actions>
						<app-button type="submit" small color="success" v-if="isNew">Create</app-button>
						<app-button type="submit" small color="primary" v-else>Save</app-button>
						<app-button type="cancel" small :to="{ name: 'market-surveys' }">Cancel</app-button>
					</app-section-actions>
					<div class="title">Basic informations</div>
					<app-row>
						<app-cell cols="3">
							<input-text
								label="Name"
								v-model="survey.name"
								required
								:errors="errors.name"
							/>
						</app-cell>
						<app-cell cols="3">
							<input-select
								label="City"
								v-model="survey.market_city"
								:items="cities"
								required
								:disabled="survey.sent"
								:errors="errors.market_city"
							/>
						</app-cell>
					</app-row>
					<app-divider class="ma-4" />
					<div class="title">Listings</div>
					<app-row>
						<app-cell cols="3">
							<input-combobox
								label="Add Listings"
								multiple
								:disabled="survey.sent"
								:items="filteredListings"
								v-model="selectedListings"
							/>
						</app-cell>
						<app-cell cols="1">
							<app-button
								type="button"
								color="success"
								outlined
								:disabled="!selectedListings.length"
								@click="addChildren('listings', 'selectedListings')"
							>Add</app-button>
						</app-cell>
					</app-row>
					<div class="simple-table pa-4">
						<template v-if="survey.listings.length">
							<app-row
								v-for="(listing, i) in survey.listings"
								:key="`listing-${i}`"
							>
								<app-cell cols="1">
									<strong>#{{ i + 1 }}</strong>
								</app-cell>
								<app-cell cols="8">
									{{ listingsMap[listing] || 'NOT FOUND' }}
								</app-cell>
								<app-cell v-if="!survey.sent" cols="3" class="text-right">
									<app-button
										icon="fa-arrow-up"
										table
										color="primary"
										icon-only
										:disabled="i <= 0"
										@click="swapChildren('listings', i, i - 1)"
									/>
									<app-button
										icon="fa-arrow-down"
										table
										color="primary"
										icon-only
										:disabled="i >= survey.listings.length - 1"
										@click="swapChildren('listings', i, i + 1)"
									/>
									<app-button
										icon="fa-trash"
										table
										color="error"
										icon-only
										@click="removeChild('listings', i)"
									/>
								</app-cell>
							</app-row>
						</template>
						<template v-else>
							<app-row>
								<app-cell class="text-center">No listings</app-cell>
							</app-row>
						</template>
					</div>
					<app-divider class="ma-4" />
					<div class="title">Participants</div>
					<app-row>
						<app-cell cols="3">
							<input-combobox
								label="Add Participants"
								multiple
								:disabled="survey.sent"
								:items="filteredParticipants"
								v-model="selectedParticipants"
							/>
						</app-cell>
						<app-cell cols="1">
							<app-button
								type="button"
								color="success"
								outlined
								:disabled="!selectedParticipants.length"
								@click="addChildren('participants', 'selectedParticipants')"
							>Add</app-button>
						</app-cell>
					</app-row>
						<div class="simple-table pa-4">
							<template v-if="survey.participants.length">
								<app-row v-for="(participant, i) in survey.participants" :key="`participant-${i}`">
									<app-cell cols="1">
										<strong>#{{ i + 1 }}</strong>
									</app-cell>
									<app-cell cols="8">
										{{ participantsMap[participant] || 'NOT FOUND' }}
									</app-cell>
									<app-cell v-if="!survey.sent" cols="3" class="text-right">
										<app-button
											icon="fa-arrow-up"
											table
											color="primary"
											icon-only
											:disabled="i <= 0"
											@click="swapChildren('participants', i, i - 1)"
										/>
										<app-button
											icon="fa-arrow-down"
											table
											color="primary"
											icon-only
											:disabled="i >= survey.participants.length - 1"
											@click="swapChildren('participants', i, i + 1)"
										/>
										<app-button
											icon="fa-trash"
											table
											color="error"
											icon-only
											@click="removeChild('participants', i)"
										/>
									</app-cell>
								</app-row>
							</template>
							<template v-else>
							<app-row>
								<app-cell class="text-center">No participants</app-cell>
							</app-row>
						</template>
						</div>
				</app-section>
			</app-form>
		</app-page>
	</div>
</template>

<script>
export default {
	async created() {
		this.$store.dispatch('app/startLoading')
		try {
			if (this.id !== 'new') {
				this.survey = await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('surveys')
					.to(this.id)
					.get()
			}

			const datasources = await this.$ravello.graphql('listings')
				.parent(this.$ravello.graphql('market', this.marketId))
				.datasource('mls', '_id', { extraFields: ['address'] })
				.sort('-id, mls')
				.filter('address', { ne: '' })
				.filter('or', [{
					survey: { exists: false },
					validated: { exists: false },
				}, {
					survey: { exists: false },
					validated: false,
				}, {
					_id: this.survey.listings,
				}])

				.union(this.$ravello.graphql('participants')
					.parent(this.$ravello.graphql('market', this.marketId))
					.sort('last_name,first_name')
					.datasource('last_name', '_id', { extraFields: ['first_name'] }))
				.union(this.$ravello.graphql('cities')
					.parent(this.$ravello.graphql('market', this.marketId))
					.datasource()
					.sort('name'))
				.query(true)

			this.listings = datasources.listings.map(listing => ({
				...listing,
				text: `${listing.address} (${listing.text})`,
			}))
			this.listingsMap = this.listings.reduce((acc, listing) => ({
				...acc,
				[listing.value]: listing.text,
			}), {})
			this.participants = datasources.participants.map(participant => ({
				...participant,
				text: `${participant.text}, ${participant.first_name}`,
			}))
			this.participantsMap = this.participants.reduce((acc, participant) => ({
				...acc,
				[participant.value]: participant.text,
			}), {})
			this.cities = datasources.cities
		} catch (err) {
			this.$notifications.pushAlert('error', err.data.message)
		}
		this.$store.dispatch('app/stopLoading')
	},
	data() {
		return {
			survey: {
				name: '',
				listings: [],
				participants: [],
				market_city: null,
				sent: false,
			},
			cities: [],
			selectedListings: [],
			selectedParticipants: [],
			listings: [],
			listingsMap: {},
			participants: [],
			participantsMap: {},
			errors: {},
		}
	},
	computed: {
		marketId() {
			return this.$route.params.market
		},
		id() {
			return this.$route.params.survey
		},
		isNew() {
			return this.id === 'new'
		},
		filteredListings() {
			return this.listings.filter(listing => !this.survey.listings.includes(listing.value))
		},
		filteredParticipants() {
			return this.participants
				.filter(participant => !this.survey.participants.includes(participant.value))
		},
	},
	methods: {
		async onSubmit() {
			this.$store.dispatch('app/startLoading')

			const request = this.$ravello.request()
				.v(2)
				.to('markets')
				.to(this.marketId)
				.to('surveys')

			const payload = {
				name: this.survey.name,
				listings: this.survey.listings,
				participants: this.survey.participants,
				market_city: this.survey.market_city,
			}

			try {
				if (this.isNew) {
					await request.create(payload)
				} else {
					await request
						.to(this.id)
						.update(payload)
				}
				this.$store.dispatch('app/stopLoading')
				this.$notifications.pushAlert('success', `Survey successfully ${this.isNew ? 'added' : 'updated'}`)
				this.$router.push({ name: 'market-surveys' })
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
				if (err.status === 400) {
					this.errors = err.data.details || {}
				}
				this.$store.dispatch('app/stopLoading')
			}
		},

		addChildren(name, selectedName) {
			this.survey[name] = [...this.survey[name], ...this[selectedName]]
			this[selectedName] = []
		},
		swapChildren(name, src, dest) {
			const swap = this.survey[name][src]
			// Needed to trigger the observable
			this.survey[name] = this.survey[name].map((entity, i) => {
				switch (i) {
				case src:
					return this.survey[name][dest]
				case dest:
					// Mapping should iterate in order
					return swap
				default:
					return entity
				}
			})
		},
		removeChild(name, position) {
			this.survey[name].splice(position, 1)
		},
	},
}
</script>

<style scoped>
	.marketmath-single-survey .simple-table >>> .row {
		border-top: 1px solid #e6e6e6;
	}
</style>
